import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getSession } from '@vitesse/base-ui';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import { mayReadValues } from './integration-access';

/*
 * <vitesse-integration-values-lock> — the one-line explanation for a grid whose
 * value column is empty on purpose.
 *
 * A person with integration:browse but not integration:read sees the Paths tab
 * (the catalogue is theirs) with no live values (those are not). Without this
 * chip that reads as a broken integration — nothing errors, because the UI
 * never opened the stream it knew would be refused (integration-access.ts).
 * <vitesse-gated> cannot say it: that element shows its CHILDREN when allowed
 * and a lock when refused, and here there is no control to show — the values
 * simply are not there.
 *
 * Renders nothing when values may be read, so the common case pays no chrome.
 * Its own shadow root, so it looks the same in the topic viewport (light DOM)
 * and the endpoint viewport (shadow DOM). The tooltip is the session's own
 * refusal sentence — the wording the server would use — so the chip and the
 * eventual error cannot disagree.
 */
@customElement('vitesse-integration-values-lock')
export class IntegrationValuesLock extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      color: var(--text-secondary);
      border: 1px dashed var(--border-color);
      border-radius: 5px;
      padding: 2px 7px;
      white-space: nowrap;
      cursor: help;
    }
  `;

  @state() private tick = 0;
  #off?: () => void;

  constructor() {
    super();
    new LocaleController(this);
  }

  connectedCallback(): void {
    super.connectedCallback();
    // A grant or revocation mid-session must move the chip without a reload —
    // the "revoked mid-session" cell of the gating test matrix.
    this.#off = getSession()?.onChange(() => (this.tick += 1));
  }
  disconnectedCallback(): void {
    this.#off?.();
    this.#off = undefined;
    super.disconnectedCallback();
  }

  render() {
    const session = getSession();
    if (mayReadValues(session)) return nothing;
    const why = session?.whyRpc(Integrations.method.read) ?? '';
    return html`<span class="chip" role="note" title=${why}>
      <span aria-hidden="true">🔒</span>${t('integration.valuesLocked')}
    </span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-integration-values-lock': IntegrationValuesLock;
  }
}
